// /frontend/src/pages/product/ProductDetailPage.jsx (Clean version with proper function order)
import { Link, useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Layout from '../../components/layout/Layout';
import ProductCard from '../../components/product/ProductCard';
import { FaShoppingCart, FaHeart, FaExpand, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { productService } from '../../services/productService';
import { cartService } from '../../services/cartService';
import { wishlistService } from '../../services/wishlistService';

const ProductDetailPage = () => {
  const { productId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Get authentication state from Redux store
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  
  // All state definitions
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('description');
  const [addingToCart, setAddingToCart] = useState(false);
  const [addToCartSuccess, setAddToCartSuccess] = useState(false);
  const [addToCartError, setAddToCartError] = useState(null);
  const [addingToWishlist, setAddingToWishlist] = useState(false);
  const [addToWishlistSuccess, setAddToWishlistSuccess] = useState(false);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isZoomOpen, setIsZoomOpen] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
  
  // Helper functions
  const getContentType = () => {
    if (!product) return 'none';
    
    console.log('=== getContentType DEBUG ===');
    
    // Check for 3D models first (variantModels or HTML in modelUrl)
    if (product.variantModels && Object.keys(product.variantModels).length > 0) {
      console.log('Content type: 3D model (variantModels)');
      return '3d';
    }
    
    if (product.modelUrl && product.modelUrl.includes('.html')) {
      console.log('Content type: 3D model (HTML in modelUrl)');
      return '3d';
    }
    
    // Check for images (images array or JPG in modelUrl)
    if (product.images && product.images.length > 0) {
      console.log('Content type: images (images array)');
      return 'images';
    }
    
    if (product.modelUrl && product.modelUrl.includes('.JPG')) {
      console.log('Content type: images (JPG in modelUrl)');
      return 'images';
    }
    
    console.log('Content type: none');
    return 'none';
  };
  
  const getAllImages = () => {
    if (!product) return [];
    
    let allImages = [];
    
    // Add images from images array
    if (product.images && product.images.length > 0) {
      allImages = [...product.images];
    }
    
    // Add images from modelUrl (split by semicolon) - only JPG files
    if (product.modelUrl && product.modelUrl.includes('.JPG')) {
      const modelImages = product.modelUrl.split(';')
        .map(url => url.trim())
        .filter(url => url && url.includes('.JPG'));
      allImages = [...allImages, ...modelImages];
    }
    
    // Remove duplicates
    return [...new Set(allImages)];
  };
  
  const handleMouseMove = (e) => {
    if (!isZoomOpen) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Update dimensi container
    if (containerDimensions.width !== rect.width || containerDimensions.height !== rect.height) {
      setContainerDimensions({ width: rect.width, height: rect.height });
    }
    
    setZoomPosition({ x, y });
  };
  
  const handleMouseEnter = () => {
    setIsZoomOpen(true);
  };
  
  const handleMouseLeave = () => {
    setIsZoomOpen(false);
  };
  
  const handleVariantChange = (variantType, optionId) => {
    console.log('=== VARIANT CHANGE ===');
    console.log('Changing variant:', variantType, 'to:', optionId);
    setSelectedVariants(prev => {
      const newVariants = {
        ...prev,
        [variantType.toLowerCase()]: optionId
      };
      console.log('New selectedVariants:', newVariants);
      return newVariants;
    });
  };
  
  const getCurrentVariantKey = () => {
    if (!product || !product.variants) return '';
    
    const key = Object.values(selectedVariants).join('-');
    console.log('getCurrentVariantKey result:', key);
    return key;
  };
  
  const getCurrentModelUrl = () => {
    console.log('=== getCurrentModelUrl DEBUG ===');
    console.log('product exists:', !!product);
    
    if (!product) {
      console.log('No product found');
      return '';
    }
    
    const variantKey = getCurrentVariantKey();
    console.log('variantKey:', variantKey);
    console.log('selectedVariants:', selectedVariants);
    console.log('product.variantModels:', product.variantModels);
    console.log('product.modelUrl:', product.modelUrl);
    
    // Check if there's a specific model for this variant combination
    if (product.variantModels && product.variantModels[variantKey]) {
      console.log('Found variant model for key:', variantKey, '=', product.variantModels[variantKey]);
      return product.variantModels[variantKey];
    }
    
    // Fall back to default model URL - but only if it's HTML for 3D models
    if (getContentType() === '3d') {
      console.log('Using fallback modelUrl for 3D:', product.modelUrl);
      return product.modelUrl;
    }
    
    console.log('No valid 3D model URL found');
    return '';
  };
  
  const getCurrentStock = () => {
    if (!product) return 0;
    
    const variantKey = getCurrentVariantKey();
    
    if (product.variantStock && product.variantStock[variantKey] !== undefined) {
      return product.variantStock[variantKey];
    }
    
    return product.variantStock?.default || product.stock || 0;
  };
  
  const calculatePrice = () => {
    if (!product || !product.variants) return { price: product?.price || 0, salePrice: product?.salePrice || 0 };
    
    let additionalCost = 0;
    
    Object.entries(selectedVariants).forEach(([variantType, optionId]) => {
      const variant = product.variants.find(v => v.name.toLowerCase() === variantType);
      if (variant) {
        const option = variant.options.find(o => o.id === optionId);
        if (option && option.priceDifference) {
          additionalCost += option.priceDifference;
        }
      }
    });
    
    return {
      price: product.price + additionalCost,
      salePrice: product.isOnSale ? product.salePrice + additionalCost : null
    };
  };
  
  const openFullScreenView = () => {
    const modelUrl = getCurrentModelUrl();
    if (modelUrl) {
      window.open(modelUrl, '_blank', 'fullscreen=yes');
    }
  };
  
  const handleAddToCart = async () => {
    if (!isAuthenticated || !user) {
      sessionStorage.setItem('redirectAfterLogin', `/product/${productId}`);
      navigate('/login');
      return;
    }
    
    if (user.role === 'admin') {
      setAddToCartError('Admin users cannot add items to cart. Please login as a regular user.');
      return;
    }
    
    try {
      setAddingToCart(true);
      setAddToCartError(null);
      
      await cartService.addToCart(productId, quantity, selectedVariants);
      
      setAddToCartSuccess(true);
      setTimeout(() => setAddToCartSuccess(false), 3000);
      
    } catch (err) {
      console.error('Add to cart error:', err);
      setAddToCartError(err.message || 'Failed to add to cart');
    } finally {
      setAddingToCart(false);
    }
  };
  
  const handleAddToWishlist = async () => {
    if (!isAuthenticated || !user) {
      sessionStorage.setItem('redirectAfterLogin', `/product/${productId}`);
      navigate('/login');
      return;
    }
    
    try {
      setAddingToWishlist(true);
      
      if (isInWishlist) {
        await wishlistService.removeFromWishlist(productId);
        setIsInWishlist(false);
      } else {
        await wishlistService.addToWishlist(productId);
        setIsInWishlist(true);
        setAddToWishlistSuccess(true);
        setTimeout(() => setAddToWishlistSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Wishlist operation failed:', err);
    } finally {
      setAddingToWishlist(false);
    }
  };
  
  // Calculate current values
  const currentPrices = product ? calculatePrice() : { price: 0, salePrice: 0 };
  const currentStock = getCurrentStock();
  const isBuyer = isAuthenticated && user && user.role !== 'admin';
  
  // Fetch product from API and check if in wishlist
  useEffect(() => {
    const fetchProductData = async () => {
      try {
        setLoading(true);
        const productData = await productService.getProductById(productId);
        console.log('=== FETCHED PRODUCT DATA ===');
        console.log('Full product data:', productData);
        console.log('variantModels:', productData.variantModels);
        console.log('modelUrl:', productData.modelUrl);
        console.log('images:', productData.images);
        console.log('variants:', productData.variants);
        setProduct(productData);
        
        // Initialize selected variants with defaults
        if (productData.variants) {
          const initialVariants = {};
          productData.variants.forEach(variant => {
            const defaultOption = variant.options.find(option => option.default) || variant.options[0];
            initialVariants[variant.name.toLowerCase()] = defaultOption.id;
          });
          console.log('Initial variants:', initialVariants);
          setSelectedVariants(initialVariants);
        }
        
        // Check if product is in wishlist (if user is authenticated)
        if (isAuthenticated) {
          try {
            const wishlistStatus = await wishlistService.isInWishlist(productId);
            setIsInWishlist(wishlistStatus);
          } catch (err) {
            console.error('Error checking wishlist status:', err);
          }
        }
        
        // Fetch related products based on category
        if (productData.category) {
          try {
            setLoadingRelated(true);
            const relatedResponse = await productService.getProductsByCategory(productData.category);
            if (relatedResponse && relatedResponse.products) {
              const filteredRelated = relatedResponse.products
                .filter(p => p._id !== productId)
                .slice(0, 4);
              setRelatedProducts(filteredRelated);
            }
          } catch (relatedErr) {
            console.error('Error fetching related products:', relatedErr);
          } finally {
            setLoadingRelated(false);
          }
        }
        
        setLoading(false);
      } catch (err) {
        setError(err.message || 'Failed to load product');
        setLoading(false);
      }
    };
    
    fetchProductData();
  }, [productId, isAuthenticated]);
  
  // Debug: Log current model URL whenever it changes
  useEffect(() => {
    if (product) {
      const contentType = getContentType();
      console.log('=== CONTENT TYPE ===', contentType);
      if (contentType === '3d') {
        const modelUrl = getCurrentModelUrl();
        console.log('=== CURRENT MODEL URL ===', modelUrl);
      } else if (contentType === 'images') {
        const images = getAllImages();
        console.log('=== AVAILABLE IMAGES ===', images);
      }
    }
  }, [product, selectedVariants]);
  
  if (loading) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="animate-pulse">
            <div className="h-96 bg-gray-200 rounded mb-6"></div>
            <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
            <div className="h-10 bg-gray-200 rounded w-1/4"></div>
          </div>
        </div>
      </Layout>
    );
  }
  
  if (error) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-12 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Product</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="inline-block px-6 py-3 bg-teal-800 text-white rounded hover:bg-teal-900 transition-colors"
          >
            Try Again
          </button>
        </div>
      </Layout>
    );
  }
  
  if (!product) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-12 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Product not found</h2>
          <p className="text-gray-600 mb-6">The product you're looking for doesn't exist or has been removed.</p>
          <Link 
            to="/browse" 
            className="inline-block px-6 py-3 bg-teal-800 text-white rounded hover:bg-teal-900 transition-colors"
          >
            Browse Products
          </Link>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 pt-6">
        <Link 
          to="/browse" 
          className="inline-flex items-center text-teal-800 hover:text-teal-900 font-medium mb-4"
        >
          <FaChevronLeft className="mr-2" /> Back to Collection
        </Link>
      </div>
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 gap-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">{product.name}</h1>
          </div>
          
          {/* Dynamic Content Section - Images OR 3D Model */}
          <div className="relative">
            <div className="w-full bg-gray-100 rounded-lg overflow-hidden mb-4 relative" style={{ height: "450px" }}>
              {/* Debug info
              <div className="absolute top-0 left-0 bg-red-500 text-white p-2 z-50 text-xs max-w-md">
                DEBUG: contentType = {getContentType()}<br/>
                availableImages = {getAllImages().length}<br/>
                modelUrl = {getCurrentModelUrl() || 'NULL'}
              </div> */}
              
              {getContentType() === 'images' ? (
                /* Image Gallery Mode */
                <div className="relative w-full h-full">
                  {/* Main Image Display with Rectangular Magnifier */}
                  <div 
                    className="relative w-full h-full cursor-crosshair overflow-hidden"
                    onMouseMove={handleMouseMove}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    <img 
                      src={getAllImages()[currentImageIndex]} 
                      alt={`${product.name} - Image ${currentImageIndex + 1}`}
                      className="w-full h-full object-contain"
                    />
                    
                    {/* Rectangular Magnifier Lens */}
                    {isZoomOpen && (
                      <div
                        className="absolute border-2 border-white shadow-lg pointer-events-none"
                        style={{
                          width: '200px',
                          height: '150px',
                          left: Math.min(
                            Math.max(zoomPosition.x - 100, 0), 
                            (containerDimensions.width || 450) - 200
                          ),
                          top: Math.min(
                            Math.max(zoomPosition.y - 75, 0), 
                            (containerDimensions.height || 450) - 150
                          ),
                          backgroundImage: `url(${getAllImages()[currentImageIndex]})`,
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '400% 400%',
                          backgroundPosition: `${((zoomPosition.x / (containerDimensions.width || 450)) * 100)}% ${((zoomPosition.y / (containerDimensions.height || 450)) * 100)}%`,
                          zIndex: 1000,
                          borderRadius: '8px'
                        }}
                      />
                    )}
                  </div>
                  
                  {/* Navigation Arrows (only show if multiple images) */}
                  {getAllImages().length > 1 && (
                    <>
                      <button 
                        onClick={() => setCurrentImageIndex(prev => 
                          prev === 0 ? getAllImages().length - 1 : prev - 1
                        )}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-90 text-gray-800 p-3 rounded-full shadow hover:bg-opacity-100 transition-all z-10"
                      >
                        <FaChevronLeft className="text-xl" />
                      </button>
                      
                      <button 
                        onClick={() => setCurrentImageIndex(prev => 
                          prev === getAllImages().length - 1 ? 0 : prev + 1
                        )}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-90 text-gray-800 p-3 rounded-full shadow hover:bg-opacity-100 transition-all z-10"
                      >
                        <FaChevronRight className="text-xl" />
                      </button>
                    </>
                  )}
                  
                  {/* Zoom Instructions */}
                  <div className="absolute top-4 right-4 bg-black bg-opacity-70 text-white px-3 py-1 rounded text-sm z-10">
                    Hover to zoom
                  </div>
                  
                  {/* Image Counter */}
                  {getAllImages().length > 1 && (
                    <div className="absolute bottom-4 right-4 bg-black bg-opacity-70 text-white px-3 py-1 rounded text-sm z-10">
                      {currentImageIndex + 1} / {getAllImages().length}
                    </div>
                  )}
                </div>
              ) : getContentType() === '3d' && getCurrentModelUrl() ? (
                /* 3D Model Mode */
                <div className="relative w-full h-full">
                  <iframe 
                    src={getCurrentModelUrl()} 
                    title={`3D Model of ${product.name}`}
                    className="w-full h-full border-0"
                    allowFullScreen={true}
                    loading="lazy"
                  ></iframe>
                  
                  {/* Full Screen Button */}
                  <button 
                    onClick={openFullScreenView}
                    className="absolute bottom-4 right-4 bg-white bg-opacity-90 text-teal-800 p-3 rounded-full shadow hover:bg-opacity-100 transition-all"
                    title="View in Full Screen"
                  >
                    <FaExpand className="text-xl" />
                  </button>
                  
                  {/* 3D Model Instructions
                  <div className="absolute top-4 right-4 bg-black bg-opacity-70 text-white px-3 py-1 rounded text-sm">
                    Click and drag to rotate
                  </div> */}
                </div>
              ) : (
                /* No content available */
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-500">No images or 3D model available</p>
                </div>
              )}
            </div>
            
            {/* Thumbnail Navigation (only show in image mode if multiple images) */}
            {getContentType() === 'images' && getAllImages().length > 1 && (
              <div className="flex space-x-2 overflow-x-auto pb-2">
                {getAllImages().map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      index === currentImageIndex 
                        ? 'border-teal-800' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <img 
                      src={image} 
                      alt={`${product.name} thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Variant Selection */}
          {product.variants && product.variants.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Options</h2>
              <div className="space-y-6">
                {product.variants.map((variant) => (
                  <div key={variant.name} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {variant.name}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {variant.options.map((option) => {
                        const isSelected = selectedVariants[variant.name.toLowerCase()] === option.id;
                        const displayPrice = option.priceDifference 
                          ? option.priceDifference > 0 
                            ? `+$${option.priceDifference}` 
                            : `-$${Math.abs(option.priceDifference)}`
                          : null;
                        
                        return (
                          <button
                            key={option.id}
                            onClick={() => handleVariantChange(variant.name, option.id)}
                            className={`px-4 py-2 border rounded-md text-sm font-medium focus:outline-none 
                              ${isSelected 
                                ? 'border-teal-800 bg-teal-50 text-teal-800' 
                                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}
                            `}
                          >
                            {option.name}
                            {displayPrice && <span className="ml-1 text-xs">{displayPrice}</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Authentication Alert for Non-logged in Users */}
          {!isAuthenticated && (
            <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded mb-4 flex items-center">
              <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <span className="font-medium">Please log in to purchase:</span>
                <span className="block mt-1">You need to be logged in as a customer to add items to your cart.</span>
                <div className="mt-2">
                  <Link to="/login" className="text-blue-800 font-medium underline">
                    Log in
                  </Link>
                  <span className="mx-2">or</span>
                  <Link to="/register" className="text-blue-800 font-medium underline">
                    Register
                  </Link>
                </div>
              </div>
            </div>
          )}
          
          {/* Admin Role Alert */}
          {isAuthenticated && user && user.role === 'admin' && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded mb-4 flex items-center">
              <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <span className="font-medium">Admin account detected:</span>
                <span className="block mt-1">Admin users cannot add items to cart. Please log in as a regular user to make purchases.</span>
              </div>
            </div>
          )}
          
          {/* Status Messages */}
          {addToCartSuccess && (
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded mb-4 flex items-center">
              <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Added to cart successfully!</span>
            </div>
          )}
          
          {addToWishlistSuccess && (
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded mb-4 flex items-center">
              <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Added to wishlist successfully!</span>
            </div>
          )}
          
          {addToCartError && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-4 flex items-center">
              <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span>{addToCartError}</span>
            </div>
          )}
          
          {/* Product Actions */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {/* Quantity Selector */}
            <div className="md:col-span-1">
              <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
              <div className="flex border border-gray-300 rounded overflow-hidden">
                <button 
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600"
                  disabled={!isBuyer}
                >
                  -
                </button>
                <input
                  type="number"
                  id="quantity"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-full text-center border-0 focus:ring-0"
                  min="1"
                  max={currentStock}
                  disabled={!isBuyer}
                />
                <button 
                  onClick={() => setQuantity(Math.min(currentStock, quantity + 1))}
                  className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600"
                  disabled={!isBuyer}
                >
                  +
                </button>
              </div>
              <div className="text-sm text-gray-500 mt-1">
                {currentStock > 10 ? 'In stock' : currentStock > 0 ? `Only ${currentStock} left!` : 'Out of stock'}
              </div>
            </div>
            
            {/* Add to Cart and Wishlist */}
            <div className="md:col-span-3 flex items-center gap-4">
              {/* Add to Cart Button */}
              {!isAuthenticated ? (
                <Link 
                  to="/login"
                  className="flex-1 bg-gray-700 hover:bg-gray-800 text-white font-medium py-3 px-6 rounded flex items-center justify-center transition-colors"
                  onClick={() => sessionStorage.setItem('redirectAfterLogin', `/product/${productId}`)}
                >
                  <FaShoppingCart className="mr-2" />
                  Log in to Purchase
                </Link>
              ) : user.role === 'admin' ? (
                <button 
                  className="flex-1 bg-gray-300 text-gray-500 font-medium py-3 px-6 rounded flex items-center justify-center cursor-not-allowed"
                  disabled
                >
                  <FaShoppingCart className="mr-2" />
                  Admin Cannot Purchase
                </button>
              ) : (
                <button 
                  onClick={handleAddToCart}
                  className={`flex-1 font-medium py-3 px-6 rounded flex items-center justify-center transition-colors ${
                    currentStock === 0 
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                      : addingToCart
                        ? 'bg-teal-600 text-white cursor-wait'
                        : 'bg-teal-800 hover:bg-teal-900 text-white'
                  }`}
                  disabled={currentStock === 0 || addingToCart}
                >
                  {addingToCart ? (
                    <>
                      <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      Adding...
                    </>
                  ) : (
                    <>
                      <FaShoppingCart className="mr-2" />
                      {currentStock === 0 ? 'Out of Stock' : 'Add to Cart'}
                    </>
                  )}
                </button>
              )}
              
              {/* Wishlist Button */}
              {!isAuthenticated ? (
                <Link
                  to="/login"
                  className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-3 px-4 rounded flex items-center justify-center transition-colors"
                  onClick={() => sessionStorage.setItem('redirectAfterLogin', `/product/${productId}`)}
                >
                  <FaHeart className="mr-2" />
                  Wishlist
                </Link>
              ) : (
                <button 
                  onClick={handleAddToWishlist}
                  disabled={addingToWishlist}
                  className={`bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-3 px-4 rounded flex items-center justify-center transition-colors ${
                    isInWishlist ? 'bg-pink-100 text-pink-600 hover:bg-pink-200' : ''
                  }`}
                >
                  {addingToWishlist ? (
                    <div className="animate-spin mr-2 h-4 w-4 border-2 border-gray-600 border-t-transparent rounded-full"></div>
                  ) : (
                    <FaHeart className={`mr-2 ${isInWishlist ? 'text-pink-600' : ''}`} />
                  )}
                  {isInWishlist ? 'In Wishlist' : 'Add to Wishlist'}
                </button>
              )}
            </div>
          </div>
          
          {/* Tabs for Description and Details */}
          <div>
            <div className="border-b border-gray-200 mb-6">
              <nav className="flex space-x-8">
                <button
                  onClick={() => setActiveTab('description')}
                  className={`pb-4 font-medium text-sm ${
                    activeTab === 'description'
                      ? 'border-b-2 border-teal-800 text-teal-800'
                      : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Description
                </button>
                <button
                  onClick={() => setActiveTab('details')}
                  className={`pb-4 font-medium text-sm ${
                    activeTab === 'details'
                      ? 'border-b-2 border-teal-800 text-teal-800'
                      : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Details
                </button>
              </nav>
            </div>
            
            {/* Tab Content */}
            <div>
              {activeTab === 'description' && (
                <div className="prose text-gray-700">
                  <p>{product.description}</p>
                </div>
              )}
              
              {activeTab === 'details' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Product Specifications</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-500">Dimensions:</span>
                          <span className="text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                            {product.dimensions && product.dimensions !== '-' ? product.dimensions : 'N/A'}
                          </span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-500">Materials:</span>
                          <span className="text-sm text-gray-900">
                            {product.materials && product.materials.length > 0 && product.materials[0] !== '-' 
                              ? product.materials.join(', ') 
                              : 'N/A'}
                          </span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-500">Colors:</span>
                          <span className="text-sm text-gray-900">
                            {product.colors && product.colors.length > 0 && product.colors[0] !== '-' 
                              ? product.colors.join(', ') 
                              : 'N/A'}
                          </span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-500">Weight:</span>
                          <span className="text-sm text-gray-900">
                            {product.weight && product.weight > 0 ? `${product.weight} lbs` : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-500">Product Code:</span>
                          <span className="text-sm text-gray-900 font-mono">
                            {product.productCode || 'N/A'}
                          </span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-500">Category:</span>
                          <span className="text-sm text-gray-900 capitalize">
                            {product.category}
                          </span>
                        </div>

                        {product.variants && product.variants.length > 0 && (
                          <div>
                            <span className="text-sm font-medium text-gray-500 block mb-2">Available Options:</span>
                            {product.variants.map((variant, index) => (
                              <div key={index} className="mb-2">
                                <span className="text-xs font-medium text-gray-700">{variant.name}:</span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {variant.options.map((option, optionIndex) => (
                                    <span
                                      key={optionIndex}
                                      className={`inline-block text-xs px-2 py-1 rounded-full ${
                                        option.default 
                                          ? 'bg-teal-100 text-teal-800' 
                                          : 'bg-gray-100 text-gray-800'
                                      }`}
                                    >
                                      {option.name}
                                      {option.default && ' (Default)'}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Related Products Section - Show for both images and 3D modes */}
          {relatedProducts.length > 0 && (
            <div className="mt-16 border-t border-gray-200 pt-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Related Products</h2>
              
              {loadingRelated ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {[...Array(4)].map((_, index) => (
                    <div key={index} className="animate-pulse">
                      <div className="bg-gray-200 aspect-square rounded mb-4"></div>
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {relatedProducts.map((relatedProduct) => (
                    <ProductCard key={relatedProduct._id} product={relatedProduct} />
                  ))}
                </div>
              )}
              
              <div className="text-center mt-8">
                <Link 
                  to={`/category/${product.category}`}
                  className="inline-block px-6 py-3 bg-teal-800 text-white rounded hover:bg-teal-900 transition-colors"
                >
                  View All {product.category.charAt(0).toUpperCase() + product.category.slice(1)}s
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ProductDetailPage;